let deferredInstallPrompt = null;
let initialized = false;
let installedThisSession = false;
let displayModeQuery = null;
const subscribers = new Set();

function getNavigator() {
  return typeof navigator === 'undefined' ? null : navigator;
}

export function isIosDevice(navigatorValue = getNavigator()) {
  if (!navigatorValue) return false;
  const userAgent = String(navigatorValue.userAgent || '');
  return /iPhone|iPad|iPod/i.test(userAgent)
    || (navigatorValue.platform === 'MacIntel' && Number(navigatorValue.maxTouchPoints) > 1);
}

export function isIosSafari(navigatorValue = getNavigator()) {
  if (!isIosDevice(navigatorValue)) return false;
  const userAgent = String(navigatorValue?.userAgent || '');
  return /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);
}

export function isStandaloneMode() {
  if (typeof window === 'undefined') return false;
  const navigatorValue = getNavigator();
  const displayModeStandalone = typeof window.matchMedia === 'function'
    && window.matchMedia('(display-mode: standalone)').matches;
  return installedThisSession || displayModeStandalone || navigatorValue?.standalone === true;
}

export function getPwaInstallState() {
  return {
    isStandalone: isStandaloneMode(),
    canPrompt: Boolean(deferredInstallPrompt),
    isIos: isIosDevice(),
    isIosSafari: isIosSafari(),
  };
}

function notifySubscribers() {
  const state = getPwaInstallState();
  subscribers.forEach((subscriber) => subscriber(state));
}

export function initializePwaInstall() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    notifySubscribers();
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    installedThisSession = true;
    notifySubscribers();
  });

  if (typeof window.matchMedia === 'function') {
    displayModeQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = () => notifySubscribers();
    if (typeof displayModeQuery.addEventListener === 'function') {
      displayModeQuery.addEventListener('change', handleDisplayModeChange);
    } else if (typeof displayModeQuery.addListener === 'function') {
      displayModeQuery.addListener(handleDisplayModeChange);
    }
  }
}

export function subscribeToPwaInstall(subscriber) {
  initializePwaInstall();
  subscribers.add(subscriber);
  subscriber(getPwaInstallState());
  return () => subscribers.delete(subscriber);
}

export async function requestPwaInstall() {
  const promptEvent = deferredInstallPrompt;
  if (!promptEvent || typeof promptEvent.prompt !== 'function') {
    return { outcome: 'unavailable' };
  }

  deferredInstallPrompt = null;
  notifySubscribers();
  await promptEvent.prompt();
  const choice = promptEvent.userChoice ? await promptEvent.userChoice : { outcome: 'dismissed' };
  if (choice?.outcome === 'accepted') installedThisSession = true;
  notifySubscribers();
  return choice;
}
